import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { RotateCcw } from "lucide-react";
import { QuizOptions } from "../components/QuizOptions";

const saveMistakes = (qid, quiz, results) =>
  results.forEach((r, i) => {
    if (r.is_correct || r.chosen_index === -1 || r.chosen_index == null) return;
    api.post("/me/mistakes", {
      source: "custom-quiz",
      source_id: qid,
      q: quiz.questions[i].q,
      options: quiz.questions[i].options,
      correct_index: r.correct_index,
      chosen_index: r.chosen_index,
      explanation: r.explanation,
    }).catch(() => { /* anonymous users have no mistake log */ });
  });

function QuestionCard({ q, qi, chosen, r, onChoose }) {
  return (
    <div className="rounded-md border border-white/10 bg-black/40 p-5" data-testid={`cq-q-${qi}`}>
      <div className="font-mono-num text-[10px] uppercase tracking-widest text-cyan-300">Q{qi + 1}</div>
      <div className="font-display mt-2 text-lg font-semibold text-white">{q.q}</div>
      <QuizOptions options={q.options} chosen={chosen} correctIndex={r?.correct_index} graded={!!r} onChoose={onChoose} testId={(oi) => `cq-q-${qi}-opt-${oi}`} />
      {r?.explanation && <div className="mt-3 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300">{r.explanation}</div>}
    </div>
  );
}

function useCustomQuiz(qid) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setQuiz(null); setResult(null); setAnswers({});
    api.get(`/custom-quizzes/${qid}`).then((r) => setQuiz(r.data)).catch(() => setErr("Quiz not found or link expired."));
  }, [qid]);

  const submit = async (studentName) => {
    if (!quiz) return;
    setBusy(true);
    const ordered = quiz.questions.map((_, i) => (i in answers ? answers[i] : -1));
    try {
      const { data } = await api.post(`/custom-quizzes/${qid}/grade`, { answers: ordered, student_name: studentName });
      setResult(data);
      saveMistakes(qid, quiz, data.results);
    } catch (e) {
      setErr(apiError(e, "Could not grade the quiz."));
    } finally {
      setBusy(false);
    }
  };
  const reset = () => { setResult(null); setAnswers({}); };
  const choose = (qi, oi) => setAnswers({ ...answers, [qi]: oi });
  return { quiz, answers, result, err, busy, submit, reset, choose };
}

function ResultBar({ result, onRetry }) {
  return (
    <>
      <div className="rounded-md border border-cyan-400/40 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-200" data-testid="cq-score">You scored <b className="font-mono-num">{result.score}</b> / {result.total}</div>
      <button onClick={onRetry} className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-3 text-sm hover:bg-white/10"><RotateCcw size={14} /> Retry</button>
      <Link to="/" className="text-xs text-slate-400 hover:text-cyan-300">Back to home</Link>
    </>
  );
}

export default function CustomQuiz() {
  const { qid } = useParams();
  const { quiz, answers, result, err, busy, submit, reset, choose } = useCustomQuiz(qid);
  const [studentName, setStudentName] = useState(() => localStorage.getItem("chemlab_student_name") || "");
  const onSubmit = () => {
    if (studentName) localStorage.setItem("chemlab_student_name", studentName);
    submit(studentName);
  };

  if (err) return <div className="rounded-md border border-red-400/40 bg-red-500/10 p-6 text-red-200">{err}</div>;
  if (!quiz) return <div className="text-slate-400">Loading quiz…</div>;
  const allAnswered = quiz.questions.every((_, i) => i in answers);

  return (
    <div className="space-y-8" data-testid="custom-quiz-root">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-cyan-300">Shared quiz</div>
        <h1 className="font-display mt-2 text-3xl font-bold sm:text-4xl">{quiz.title}</h1>
        {!result && (
          <input value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Your name (shows on the class leaderboard)" data-testid="student-name"
            className="mt-4 w-full max-w-sm rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none" />
        )}
      </div>
      <div className="space-y-4">
        {quiz.questions.map((q, qi) => (
          <QuestionCard key={`${qi}-${q.q}`} q={q} qi={qi} chosen={answers[qi]} r={result?.results[qi]} onChoose={(oi) => choose(qi, oi)} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {!result ? (
          <button onClick={onSubmit} disabled={!allAnswered || busy} data-testid="cq-submit" className="inline-flex items-center gap-2 rounded-md bg-cyan-500 px-5 py-3 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-50">Submit</button>
        ) : (
          <ResultBar result={result} onRetry={reset} />
        )}
      </div>
    </div>
  );
}
